import { useState } from 'react';
import { Package, AlertTriangle, ChevronRight, ChevronDown, RotateCcw, Key } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import type { DeploymentStats } from '@/types/metrics';

interface SpareInventoryCardProps {
  spareInventory: DeploymentStats['spareInventory'];
}

export function SpareInventoryCard({ spareInventory }: SpareInventoryCardProps) {
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  const toggleProduct = (product: string) => {
    setExpandedProduct((prev) => (prev === product ? null : product));
  };

  return (
    <Card className="dls-flex dls-flex-col dls-h-full">
      <CardHeader className="dls-pb-2">
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5 dls-text-base">
          <Package className="dls-h-4.5 dls-w-4.5 dls-text-neeve-secondary" />
          Spare Inventory
          <InfoTooltip text="Available hardware for RMA replacements and upgrades. Only non-EOL, unassigned serial numbers are counted as usable spares. Click a row to see individual serial numbers." />
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-flex dls-flex-col dls-flex-1 dls-gap-3">
        <div className="dls-rounded-md dls-bg-neeve-mint dls-p-2 dls-border dls-border-neeve-secondary/10 dls-flex dls-items-baseline dls-gap-2">
          <span className="dls-text-2xl dls-font-bold dls-text-neeve-primary">
            {spareInventory.totalSpares}
          </span>
          <span className="dls-text-sm dls-text-neeve-secondary">available for RMA</span>
        </div>

        {spareInventory.byProduct.length > 0 && (
          <div className="dls-max-h-48 dls-overflow-y-auto">
            <table className="dls-w-full dls-text-xs">
              <thead>
                <tr className="dls-text-muted-foreground dls-border-b">
                  <th className="dls-text-center dls-py-1 dls-font-medium dls-w-10">Qty</th>
                  <th className="dls-text-left dls-py-1 dls-font-medium">Hardware</th>
                  <th className="dls-text-left dls-py-1 dls-font-medium">CPU</th>
                  <th className="dls-text-center dls-py-1 dls-pr-2 dls-font-medium">RAM</th>
                </tr>
              </thead>
              <tbody>
                {spareInventory.byProduct.map((group) => {
                  const isExpanded = expandedProduct === group.product;
                  return (
                    <>
                      <tr
                        key={group.product}
                        className="dls-border-b dls-cursor-pointer hover:dls-bg-muted/50 dls-transition-colors"
                        onClick={() => toggleProduct(group.product)}
                      >
                        <td className="dls-py-1.5 dls-text-center dls-font-semibold">
                          <div className="dls-flex dls-items-center dls-justify-center dls-gap-0.5">
                            {isExpanded ? (
                              <ChevronDown className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                            ) : (
                              <ChevronRight className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                            )}
                            {group.count}
                          </div>
                        </td>
                        <td className="dls-py-1.5">
                          <div className="dls-font-medium">{group.product}</div>
                          <div className="dls-text-muted-foreground">{group.vendor}</div>
                        </td>
                        <td className="dls-py-1.5 dls-text-muted-foreground dls-truncate dls-max-w-[120px]">
                          {group.cpuModel ?? '-'}
                        </td>
                        <td className="dls-py-1.5 dls-text-center dls-pr-2 dls-text-muted-foreground">
                          {group.memorySize ? `${Math.round(group.memorySize / 1024)}GB` : '-'}
                        </td>
                      </tr>
                      {isExpanded &&
                        group.items.map((item) => (
                          <tr
                            key={item.hardwareSerialNumber}
                            className="dls-border-b last:dls-border-0 dls-bg-muted/30"
                          >
                            <td className="dls-py-1" />
                            <td className="dls-py-1 dls-font-mono dls-text-[11px]" colSpan={3}>
                              <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap">
                                <span>{item.hardwareSerialNumber}</span>
                                {item.previouslyAssigned && (
                                  <span className="dls-inline-flex dls-items-center dls-gap-0.5 dls-rounded-full dls-bg-status-info/15 dls-text-status-info dls-px-1.5 dls-py-0 dls-text-[10px] dls-font-medium">
                                    <RotateCcw className="dls-h-2.5 dls-w-2.5" />
                                    Previously Used
                                  </span>
                                )}
                                {item.sshKeyName && (
                                  <span className="dls-inline-flex dls-items-center dls-gap-0.5 dls-text-muted-foreground dls-text-[10px]">
                                    <Key className="dls-h-2.5 dls-w-2.5" />
                                    {item.sshKeyName}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="dls-flex dls-items-center dls-gap-2 dls-rounded-md dls-bg-status-warning-quarternary dls-p-2 dls-border dls-border-status-warning-tertiary dls-text-xs dls-text-status-warning dls-mt-auto dls-flex-shrink-0">
          <AlertTriangle className="dls-h-3.5 dls-w-3.5 dls-flex-shrink-0" />
          {spareInventory.eolSparesCount > 0 ? (
            <span>
              {spareInventory.eolSparesCount} spare device
              {spareInventory.eolSparesCount !== 1 ? 's are' : ' is'} EOL and not recommended for
              deployment
            </span>
          ) : (
            <span>All spare devices are current models and eligible for deployment.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
